import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getChartTheme } from '@/libs/recharts/theme/theme';

export interface IntegrationUsageDayView {
    /** `YYYY-MM-DD` in the workspace's timezone. Not a timestamp. */
    day: string;
    api: number;
    mcp: number;
}

interface IntegrationUsageChartProps {
    days?: IntegrationUsageDayView[] | null;
    /** Which series to plot. The two quotas are separate, so each card charts only its own. */
    kind: 'api' | 'mcp';
    /**
     * `YYYY-MM-DD` the current quota period began, already bucketed in the workspace's
     * timezone by the backend. Marked on the chart so it is obvious why a rolling 31-day
     * series does not add up to the counter above it.
     */
    periodStartDay?: string | null;
    loading?: boolean;
    dataId: string;
}

/**
 * Rolling 31 days of daily usage.
 *
 * A rolling window rather than the quota period, because early in a period the latter is
 * one or two bars. The cost is that the bars do not sum to the counter above, so the day
 * the quota reset is marked on the axis to explain the difference in place.
 *
 * Bars, not a line: these are counts on discrete days, and a line would imply usage
 * between them. The series arrives zero-filled from the backend, so a quiet month renders
 * as visible gaps rather than being compressed into a busy-looking shape.
 */
export default function IntegrationUsageChart({
    days,
    kind,
    periodStartDay,
    loading,
    dataId,
}: IntegrationUsageChartProps) {
    const { t } = useTranslation();
    const theme = useTheme();
    const chart = getChartTheme(theme.palette.mode === 'dark' ? 'dark' : 'light');

    if (loading && !days) {
        return <Skeleton variant="rectangular" height={140} data-id={`${dataId}-loading`} />;
    }

    if (!days?.length) return null;

    // Nothing used yet this period: an empty axis reads as broken, so say so instead.
    if (days.every((d) => d[kind] === 0)) {
        return (
            <Typography variant="body2" color="text.secondary" data-id={`${dataId}-empty`}>
                {t('settings.workspace.api.usage.chart.empty')}
            </Typography>
        );
    }

    // Show the day-of-month only: the period never spans more than ~31 days, so the full
    // date on every tick is noise.
    const dayLabel = (day: string) => String(Number(day.slice(8, 10)));

    return (
        <Box data-id={dataId}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('settings.workspace.api.usage.chart.title')}
            </Typography>
            <Box height={140}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={days} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke={chart.colors.border} vertical={false} />
                        <XAxis
                            dataKey="day"
                            tickFormatter={dayLabel}
                            tick={{ fontSize: 11 }}
                            stroke={chart.colors.border}
                            interval="preserveStartEnd"
                            minTickGap={8}
                        />
                        <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11 }}
                            stroke={chart.colors.border}
                            width={40}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: chart.tooltip.backgroundColor,
                                borderColor: chart.tooltip.borderColor,
                                color: chart.tooltip.textColor,
                                fontSize: 12,
                            }}
                            labelFormatter={(day) => String(day)}
                            formatter={(value: number) => [
                                value.toLocaleString(),
                                t(`settings.workspace.${kind}.usage.label`),
                            ]}
                        />
                        {periodStartDay && days.some((d) => d.day === periodStartDay) && (
                            <ReferenceLine
                                x={periodStartDay}
                                stroke={chart.colors.highlight}
                                strokeDasharray="3 3"
                                label={{
                                    value: t('settings.workspace.api.usage.chart.reset'),
                                    position: 'insideTopLeft',
                                    fontSize: 10,
                                    fill: chart.colors.highlight,
                                }}
                            />
                        )}
                        <Bar dataKey={kind} fill={chart.colors.primary} radius={[2, 2, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}
